import React, { useState, useEffect } from 'react';
import { ChevronLeft, Check, Sparkles, Calendar, Clock, BrainCircuit, Download, Share, PlusSquare, Smartphone } from 'lucide-react';

interface OnboardingProps {
  onComplete: (name: string) => void;
}

const Onboarding: React.FC<OnboardingProps> = ({ onComplete }) => {
  // Check if PWA is already installed
  const isStandalone = typeof window !== 'undefined' && (
      window.matchMedia('(display-mode: standalone)').matches || 
      (window.navigator as any).standalone === true
  );

  // If installed, start at step 2 (Welcome). If not, start at step 1 (Install).
  const [step, setStep] = useState(isStandalone ? 2 : 1);
  const [name, setName] = useState('');
  const [isExiting, setIsExiting] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check for iOS
    const isIosDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(isIosDevice);

    const handler = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleNext = () => {
    if (step === 3 && !name.trim()) return;
    setStep(step + 1);
  };

  const handleFinish = () => {
    setIsExiting(true);
    setTimeout(() => {
      onComplete(name || 'کاربر عزیز');
    }, 500); // Wait for exit animation
  };

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
        handleNext();
      }
    }
  };

  const steps = [
    {
      id: 1, // Install Step
      content: (
        <div className="text-center space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
           <div className="w-24 h-24 bg-blue-100 dark:bg-blue-900/30 rounded-3xl mx-auto flex items-center justify-center text-blue-600 dark:text-blue-400 mb-6 relative">
              <Download size={40} className="animate-bounce" />
              {/* Device Frame */}
              <div className="absolute -bottom-2 -right-2 bg-white dark:bg-slate-800 p-2 rounded-xl shadow-lg border border-slate-100 dark:border-slate-700">
                <Smartphone size={20} className="text-slate-600 dark:text-slate-300" />
              </div>
           </div>
           
           <h1 className="text-2xl font-bold text-slate-800 dark:text-white">نصب نسخه وب‌اپلیکیشن</h1>
           <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed max-w-xs mx-auto">
             برای دسترسی سریع‌تر و استفاده آفلاین، پیشنهاد می‌کنیم گلنس را روی دستگاه خود نصب کنید.
           </p>

           {/* IOS Instructions */}
           {isIOS && !isStandalone && (
             <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl text-sm text-slate-600 dark:text-slate-300 text-right space-y-3 border border-slate-100 dark:border-slate-700 mx-auto max-w-xs">
                <p className="font-bold flex items-center justify-end gap-2 text-slate-800 dark:text-slate-200">
                  راهنمای نصب در iOS:
                </p>
                <p className="flex items-center justify-end gap-2">
                  ۱. روی دکمه Share بزنید
                  <Share size={16} className="text-blue-500" />
                </p>
                <p className="flex items-center justify-end gap-2">
                  ۲. گزینه Add to Home Screen را انتخاب کنید
                  <PlusSquare size={16} />
                </p>
             </div>
           )}
        </div>
      )
    },
    {
      id: 2, // Welcome
      content: (
        <div className="text-center space-y-6 animate-in fade-in slide-in-from-right-8 duration-500">
          <div className="w-24 h-24 bg-primary-600 rounded-3xl mx-auto flex items-center justify-center shadow-xl shadow-primary-200 dark:shadow-none mb-8 rotate-3">
            <span className="text-5xl font-bold text-white">گ</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-800 dark:text-white">به <span className="text-primary-600">گلنس</span> خوش آمدید</h1>
          <p className="text-slate-500 dark:text-slate-400 text-lg leading-relaxed max-w-xs mx-auto">
            دستیار هوشمند شما برای برنامه‌ریزی تحصیلی، مدیریت زمان و موفقیت در امتحانات.
          </p>
        </div>
      )
    },
    {
      id: 3, // Name
      content: (
        <div className="text-center space-y-6 animate-in fade-in slide-in-from-right-8 duration-500">
          <div className="w-20 h-20 bg-indigo-100 dark:bg-indigo-900/30 rounded-full mx-auto flex items-center justify-center mb-4 text-indigo-600 dark:text-indigo-400">
             <Sparkles size={40} />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">اسم شما چیست؟</h2>
          <p className="text-slate-500 dark:text-slate-400">برای اینکه بهتر بتوانیم شما را راهنمایی کنیم، نام خود را وارد کنید.</p>
          <div className="pt-4">
            <input 
              type="text" 
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="نام شما..."
              className="w-full max-w-xs text-center bg-slate-50 dark:bg-slate-800 border-b-2 border-slate-200 dark:border-slate-700 focus:border-primary-500 outline-none py-3 text-xl text-slate-800 dark:text-white transition-colors rounded-t-lg"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && name.trim() && handleNext()}
            />
          </div>
        </div>
      )
    },
    {
      id: 4, // Features
      content: (
        <div className="space-y-8 animate-in fade-in slide-in-from-right-8 duration-500">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">ویژگی‌های کلیدی</h2>
            <p className="text-slate-500 dark:text-slate-400 text-sm">همه چیز برای موفقیت شما آماده است</p>
          </div>
          
          <div className="grid gap-4">
             <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-700/50">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-xl flex items-center justify-center shrink-0">
                  <Calendar size={24} />
                </div>
                <div className="text-right">
                  <h3 className="font-bold text-slate-800 dark:text-white">تقویم شمسی کامل</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">مدیریت رویدادها و یادداشت‌های روزانه</p>
                </div>
             </div>

             <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-700/50">
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-xl flex items-center justify-center shrink-0">
                  <BrainCircuit size={24} />
                </div>
                <div className="text-right">
                  <h3 className="font-bold text-slate-800 dark:text-white">برنامه‌ریز هوشمند</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">تنظیم خودکار برنامه درسی با اولویت‌بندی</p>
                </div>
             </div>

             <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-700/50">
                <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded-xl flex items-center justify-center shrink-0">
                  <Clock size={24} />
                </div>
                <div className="text-right">
                  <h3 className="font-bold text-slate-800 dark:text-white">ابزارهای تمرکز</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">تایمر پومودورو و ابزارهای کاهش استرس</p>
                </div>
             </div>
          </div>
        </div>
      )
    },
    {
      id: 5, // Ready
      content: (
        <div className="text-center space-y-8 animate-in fade-in slide-in-from-right-8 duration-500 py-4">
           <div className="relative w-32 h-32 mx-auto">
              <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-20"></div>
              <div className="relative w-32 h-32 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center text-green-600 dark:text-green-400">
                 <Check size={64} strokeWidth={3} />
              </div>
           </div>
           
           <div>
             <h2 className="text-3xl font-bold text-slate-800 dark:text-white mb-3">همه چیز آماده است!</h2>
             <p className="text-slate-500 dark:text-slate-400 text-lg">
               شروع کنیم به ساختن آینده‌ای درخشان، <span className="text-primary-600 font-bold">{name}</span>.
             </p>
           </div>
        </div>
      )
    }
  ];

  return (
    <div className={`fixed inset-0 z-[100] flex items-center justify-center bg-slate-100 dark:bg-slate-950 p-4 transition-opacity duration-500 ${isExiting ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
         <div className="absolute -top-[20%] -right-[20%] w-[70%] h-[70%] bg-primary-200/20 dark:bg-primary-900/20 rounded-full blur-3xl"></div>
         <div className="absolute -bottom-[20%] -left-[20%] w-[70%] h-[70%] bg-indigo-200/20 dark:bg-indigo-900/20 rounded-full blur-3xl"></div>
      </div>

      <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-3xl shadow-2xl p-8 relative overflow-hidden min-h-[500px] flex flex-col border border-slate-100 dark:border-slate-800">
         {/* Step Content */}
         <div className="flex-1 flex flex-col justify-center">
            {steps.find(s => s.id === step)?.content}
         </div>

         {/* Navigation */}
         <div className="mt-8 flex flex-col gap-4">
            
            {step === 1 ? (
               <div className="space-y-3">
                  {deferredPrompt && (
                    <button 
                      onClick={handleInstallClick}
                      className="w-full py-4 rounded-xl font-bold text-lg shadow-lg shadow-blue-200 dark:shadow-none transition-all flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white hover:scale-[1.02] active:scale-[0.98]"
                    >
                      <Download size={20} />
                      نصب گلنس
                    </button>
                  )}
                  
                  <button 
                    onClick={handleNext}
                    className={`w-full py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2 
                      ${!deferredPrompt && !isIOS ? 'bg-primary-600 text-white hover:bg-primary-700' : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-800'}
                    `}
                  >
                    {!deferredPrompt && !isIOS ? 'ادامه' : 'بگذریم'}
                    <ChevronLeft size={20} />
                  </button>
               </div>
            ) : (
               <>
                <button 
                  onClick={step === 5 ? handleFinish : handleNext}
                  disabled={step === 3 && !name.trim()}
                  className={`
                    w-full py-4 rounded-xl font-bold text-lg shadow-lg shadow-primary-200 dark:shadow-none transition-all flex items-center justify-center gap-2
                    ${step === 3 && !name.trim() 
                      ? 'bg-slate-200 dark:bg-slate-800 text-slate-400 cursor-not-allowed' 
                      : 'bg-primary-600 hover:bg-primary-700 text-white hover:scale-[1.02] active:scale-[0.98]'}
                  `}
                >
                   {step === 5 ? 'بزن بریم!' : 'ادامه'}
                   {step !== 5 && <ChevronLeft size={20} />}
                </button>

                {/* Dots */}
                <div className="flex justify-center gap-2 mt-2">
                   {[1, 2, 3, 4, 5].map(i => (
                     <div 
                       key={i} 
                       className={`h-2 rounded-full transition-all duration-300 ${i === step ? 'w-8 bg-primary-600' : 'w-2 bg-slate-200 dark:bg-slate-800'}`}
                     />
                   ))}
                </div>
               </>
            )}
         </div>
      </div>
    </div>
  );
};

export default Onboarding;